import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Search,
  Plus,
  FileText,
  Barcode,
  Upload,
  Eye,
  Ban,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import { OfflineLabtestService } from "../../../../../services/api/OfflineLabtest/offline-labtest.service";
import { OfflineLabtestListingItem } from "../../../../../services/api/OfflineLabtest/types";
import { OfflineLabtestDetailsScreen } from "./OfflineLabtestDetailsScreen";
import { CreateOfflineLabtestOrderScreen } from "./CreateOfflineLabtestOrderScreen";
import { DataTable, Column } from "../../../../DataTable/DataTable";
import { CancelConfirmationModal } from "./CancelConfirmationModal";

interface OfflineLabtestListingProps {
  clinicId: string;
}

type TabType = "booked" | "sample_collected" | "report_generated" | "cancelled";
type ViewMode = "list" | "create" | "details";

interface TableRowItem extends OfflineLabtestListingItem {
  id: number;
}

export const OfflineLabtestListing: React.FC<OfflineLabtestListingProps> = ({ clinicId }) => {
  const [view, setView] = useState<ViewMode>("list");
  const [activeTab, setActiveTab] = useState<TabType>("booked");

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Data states
  const [orders, setOrders] = useState<OfflineLabtestListingItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Selected Order for Details Screen
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch listing
  const fetchListing = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        pageSize,
        clinicId,
        status: activeTab,
      };

      if (debouncedSearch) {
        if (/^\d{10}$/.test(debouncedSearch)) {
          params.patientMobileNumber = debouncedSearch;
        } else {
          params.invoiceNumber = debouncedSearch;
        }
      }

      const res = await OfflineLabtestService.getListing(params);
      if (res.success && res.data) {
        setOrders(res.data.data || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      } else {
        setOrders([]);
        setTotal(0);
        setTotalPages(1);
      }
    } catch (error: any) {
      console.error("Error fetching offline labtest listing:", error);
      toast.error("Failed to load offline labtest orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [clinicId, activeTab, page, pageSize, debouncedSearch]);

  useEffect(() => {
    if (view === "list") {
      fetchListing();
    }
  }, [fetchListing, view]);

  // Cancel order modal state
  const [cancellingOrder, setCancellingOrder] = useState<OfflineLabtestListingItem | null>(null);
  const [cancellingLoading, setCancellingLoading] = useState(false);

  const handleConfirmCancelOrder = async () => {
    if (!cancellingOrder) return;
    try {
      setCancellingLoading(true);
      const res = await OfflineLabtestService.cancelOrder(cancellingOrder.orderId);
      if (res.success) {
        toast.success("Order cancelled successfully");
        setCancellingOrder(null);
        fetchListing();
      } else {
        toast.error(res.message || "Failed to cancel order");
      }
    } catch (err: any) {
      console.error("Cancel order error:", err);
      toast.error("Failed to cancel order");
    } finally {
      setCancellingLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "booked":
        return <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold">Booked</span>;
      case "sample_collected":
        return <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-semibold">Sample Collected</span>;
      case "report_generated":
        return <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">Report Generated</span>;
      case "cancelled":
        return <span className="px-2.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-xs font-semibold">Cancelled</span>;
      default:
        return <span className="px-2.5 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">{status}</span>;
    }
  };

  // Map orders for DataTable (requires item.id)
  const tableData: TableRowItem[] = useMemo(() => {
    return orders.map((order) => ({
      ...order,
      id: order.orderId,
    }));
  }, [orders]);

  // Define Columns for DataTable
  const columns: Column<TableRowItem>[] = [
    {
      key: "invoiceNumber",
      title: "Invoice No",
      render: (item) => <span className="text-gray-900">#{item.invoiceNumber}</span>,
    },
    {
      key: "patientName",
      title: "Patient Name",
      render: (item) => <span className="text-gray-800">{item.patientName || "N/A"}</span>,
    },
    {
      key: "patientPhone",
      title: "Mobile No",
      render: (item) => <span className="text-gray-600">{item.patientPhone}</span>,
    },
    {
      key: "totalTests",
      title: "Total Tests",
      render: (item) => <span className="text-gray-700">{item.totalTests}</span>,
    },
    {
      key: "totalAmount",
      title: "Total Amount",
      render: (item) => (
        <span className="text-emerald-700">₹{Number(item.totalAmount).toFixed(2)}</span>
      ),
    },
    {
      key: "status",
      title: "Status",
      render: (item) => getStatusBadge(item.status),
    },
    {
      key: "actions",
      title: "Actions",
      render: (item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedOrderId(item.orderId);
            setView("details");
          }}
          title="View Order Details"
          className="px-2.5 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg font-medium flex items-center gap-1 transition-colors text-xs"
        >
          <Eye className="w-3.5 h-3.5 text-gray-500" />
          <span>View Details</span>
        </button>
      ),
    },
  ];

  // Render Create Order Screen
  if (view === "create") {
    return (
      <CreateOfflineLabtestOrderScreen
        clinicId={clinicId}
        onBack={() => setView("list")}
        onSuccess={() => {
          setView("list");
          fetchListing();
        }}
      />
    );
  }

  // Render Order Details Screen
  if (view === "details" && selectedOrderId) {
    return (
      <OfflineLabtestDetailsScreen
        orderId={selectedOrderId}
        onBack={() => {
          setView("list");
          setSelectedOrderId(null);
        }}
        onRefreshListing={fetchListing}
      />
    );
  }

  // Render Master Listing Screen with DataTable
  return (
    <div className="w-full flex flex-col rounded-xl shadow-sm border border-gray-200/80 overflow-hidden">
      {/* Top Header Bar */}
      <div className="p-4 border-b border-gray-200 bg-gray-50/50">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Create Button */}
          <button
            onClick={() => setView("create")}
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-xl flex items-center gap-1.5 shadow transition-all w-fit"
          >
            <Plus className="w-4 h-4" />
            <span>Create New Order</span>
          </button>

          {/* Right Controls */}
          <div className="flex items-center gap-3 sm:ml-auto w-full sm:w-auto">
            <button
              onClick={fetchListing}
              title="Refresh Listing"
              className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-gray-200"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin text-emerald-600" : ""
                  }`}
              />
            </button>

            <div className="relative flex-1 sm:flex-none sm:w-80">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Search by Mobile Number or Invoice No..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* 4 Status Sub-Tabs */}
      <div className="px-4 border-b border-gray-200 bg-white flex items-center gap-6 overflow-x-auto">
        <button
          onClick={() => {
            setActiveTab("booked");
            setPage(1);
          }}
          className={`py-3 text-xs font-bold border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${activeTab === "booked"
            ? "border-emerald-600 text-emerald-700"
            : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
        >
          <span>Booked</span>
          {activeTab === "booked" && total > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px]">
              {total}
            </span>
          )}
        </button>

        <button
          onClick={() => {
            setActiveTab("sample_collected");
            setPage(1);
          }}
          className={`py-3 text-xs font-bold border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${activeTab === "sample_collected"
            ? "border-emerald-600 text-emerald-700"
            : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
        >
          <span>Sample Collected</span>
          {activeTab === "sample_collected" && total > 0 && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-[10px]">
              {total}
            </span>
          )}
        </button>

        <button
          onClick={() => {
            setActiveTab("report_generated");
            setPage(1);
          }}
          className={`py-3 text-xs font-bold border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${activeTab === "report_generated"
            ? "border-emerald-600 text-emerald-700"
            : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
        >
          <span>Report Generated</span>
          {activeTab === "report_generated" && total > 0 && (
            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px]">
              {total}
            </span>
          )}
        </button>

        <button
          onClick={() => {
            setActiveTab("cancelled");
            setPage(1);
          }}
          className={`py-3 text-xs font-bold border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${activeTab === "cancelled"
            ? "border-emerald-600 text-emerald-700"
            : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
        >
          <span>Cancelled</span>
          {activeTab === "cancelled" && total > 0 && (
            <span className="px-2 py-0.5 bg-rose-100 text-rose-800 rounded-full text-[10px]">
              {total}
            </span>
          )}
        </button>
      </div>

      {/* Table Listing Body with DataTable */}
      <div className="flex-1">
        <DataTable
          data={tableData}
          columns={columns}
          itemsPerPage={pageSize}
          isLoading={loading}
          onRowClick={(item) => {
            setSelectedOrderId(item.orderId);
            setView("details");
          }}
          pagination={{
            currentPage: page,
            setCurrentPage: (p) => setPage(p),
            totalPages: totalPages,
            pageSize: pageSize,
          }}
        />
      </div>

      {/* Cancel Order Confirmation Modal */}
      <CancelConfirmationModal
        isOpen={Boolean(cancellingOrder)}
        orderNumber={cancellingOrder?.invoiceNumber || cancellingOrder?.orderId}
        loading={cancellingLoading}
        onClose={() => setCancellingOrder(null)}
        onConfirm={handleConfirmCancelOrder}
      />
    </div>
  );
};
